'use client';

import { FC, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Check, Lock, Mail, User } from 'lucide-react';
import { Spinner, Modal } from 'react-bootstrap';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { commonConstant } from '@/hotdesk-ui/constants/common.constant';
import InputField from './InputField';
import PhoneField from './PhoneField';
import PrimaryButton from './PrimaryButton';
import shellStyles from './AuthShell.module.scss';
import styles from './SignUpScreen.module.scss';
import TermsAndConditions from '@/hotdesk-ui/terms-and-conditions/termsAndConditions';
import Image from 'next/image';
export interface SignUpFormValues {
  firstName: string;
  lastName?: string | null;
  phone: string;
  email: string;
  itidaId?: string | null;
  password: string;
  confirmPassword: string;
  agreed: boolean;
}
export interface SignUpServerErrors {
  email?: string;
  phone?: string;
  itidaId?: string;
  nonField?: string;
}
interface SignUpScreenProps {
  initialEmail?: string;
  isItida?: boolean;
  isLoading?: boolean;
  serverErrors?: SignUpServerErrors;
  termLink?: string;
  privacyPolicyLink?: string;
  onSubmit: (data: SignUpFormValues) => void | Promise<void>;
  onCountryDetected?: (countryCode: string) => void;
}
const SignUpScreen: FC<SignUpScreenProps> = ({
  initialEmail,
  isItida = false,
  isLoading = false,
  serverErrors,
  termLink,
  privacyPolicyLink,
  onSubmit
}) => {
  const t = useTranslations();
  const [show, setShow] = useState(false);
  const schema = yup.object().shape({
    firstName: yup.string().required(t('auth.signUp.first-name') + ' ' + t('auth.signUp.error')).min(1, t('auth.signUp.name-error')).max(100, t('auth.signUp.name-error')).matches(commonConstant.nameRegex, t('auth.signUp.name-error')),
    lastName: yup.string().nullable().test('is-valid-last-name', t('auth.signUp.name-error'), value => {
      if (!value) return true;
      return value.length >= 1 && value.length <= 100 && commonConstant.nameRegex.test(value);
    }),
    phone: yup.string().required(t('auth.signUp.phone') + ' ' + t('auth.signUp.error')).test('valid-phone', t('auth.signUp.phone-error'), value => {
      return !!value && isValidPhoneNumber(value || '');
    }),
    email: yup.string().email(t('auth.signUp.email-error')).max(100, t('auth.signUp.email-error')).required(t('auth.signUp.email') + ' ' + t('auth.signUp.error')),
    itidaId: isItida ? yup.string().nullable().optional() : yup.string().nullable(),
    password: yup.string().required(t('auth.signUp.password') + ' ' + t('auth.signUp.error')).min(8, t('auth.signUp.minLength')).matches(/\d/, t('auth.signUp.oneNumber')).matches(/[A-Z]/, t('auth.signUp.oneCapital')).matches(/[a-z]/, t('auth.signUp.oneSmall')).matches(/[@$!%*#?&.,:;\-_+=\/'"]/, t('auth.signUp.oneSpecial')).matches(/^[^{}\[\]]*$/, t('auth.signUp.nonSpecial')),
    confirmPassword: yup.string().required(t('auth.signUp.confirm-password') + ' ' + t('auth.signUp.error')).test('passwords-match', t('auth.signUp.passwordMatch'), function (value) {
      return value === this.parent.password;
    }),
    agreed: yup.boolean().required(t('auth.signUp.policy-error')).oneOf([true], t('auth.signUp.policy-error'))
  });
  const {
    register,
    handleSubmit,
    trigger,
    control,
    setValue,
    setError,
    formState: {
      errors
    }
  } = useForm<SignUpFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: initialEmail ?? '',
      itidaId: '',
      password: '',
      confirmPassword: '',
      agreed: false
    }
  });
  useEffect(() => {
    if (initialEmail) setValue('email', initialEmail);
  }, [initialEmail, setValue]);
  useEffect(() => {
    if (!serverErrors) return;
    if (serverErrors.email) {
      setError('email', {
        type: 'server',
        message: serverErrors.email
      });
    }
    if (serverErrors.phone) {
      setError('phone', {
        type: 'server',
        message: serverErrors.phone
      });
    }
    if (serverErrors.itidaId) {
      setError('itidaId', {
        type: 'server',
        message: serverErrors.itidaId
      });
    }
  }, [serverErrors, setError]);
  const submit = handleSubmit(async data => {
    await onSubmit(data);
  });
  return <>
            <h2 className={shellStyles.heading}>{t('auth.signUp.title')}</h2>
            <p className={shellStyles.subtitle}>
                {t('auth.signUp.subtitle')}
                <span className={shellStyles.subSubtitle}>{t('auth.signUp.subSubtitle')}</span>
            </p>

            <form className={styles.form} onSubmit={submit} noValidate>
                <InputField icon={<User size={16} strokeWidth={2} />} placeholder={t('auth.signUp.first-name')} autoComplete="given-name" error={errors.firstName?.message} {...register('firstName', {
        onBlur: () => trigger('firstName')
      })} data-sentry-element="InputField" data-sentry-source-file="SignUpScreen.tsx" />

                <InputField icon={<User size={16} strokeWidth={2} />} placeholder={t('auth.signUp.last-name')} autoComplete="family-name" error={errors.lastName?.message} {...register('lastName', {
        onBlur: () => trigger('lastName')
      })} data-sentry-element="InputField" data-sentry-source-file="SignUpScreen.tsx" />

                <Controller name="phone" control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <PhoneField value={value} onChange={onChange} onBlur={() => trigger('phone')} placeholder={t('auth.signUp.phone')} error={errors.phone?.message} />} data-sentry-element="Controller" data-sentry-source-file="SignUpScreen.tsx" />

                <InputField icon={<Mail size={16} strokeWidth={2} />} type="email" placeholder={t('auth.signUp.email')} autoComplete="email" error={errors.email?.message} {...register('email', {
        onBlur: () => trigger('email')
      })} data-sentry-element="InputField" data-sentry-source-file="SignUpScreen.tsx" />

                {isItida && <InputField icon={<User size={16} strokeWidth={2} />} placeholder={t('auth.signUp.itidaId')} error={errors.itidaId?.message} {...register('itidaId', {
        setValueAs: value => value || undefined,
        onBlur: () => trigger('itidaId')
      })} />}

                <InputField icon={<Lock size={16} strokeWidth={2} />} type="password" placeholder={t('auth.signUp.password')} autoComplete="new-password" showPasswordToggle error={errors.password?.message} {...register('password', {
        onBlur: () => trigger('password')
      })} data-sentry-element="InputField" data-sentry-source-file="SignUpScreen.tsx" />

                <InputField icon={<Lock size={16} strokeWidth={2} />} type="password" placeholder={t('auth.signUp.confirm-password')} autoComplete="new-password" showPasswordToggle error={errors.confirmPassword?.message} {...register('confirmPassword', {
        onBlur: () => trigger('confirmPassword')
      })} data-sentry-element="InputField" data-sentry-source-file="SignUpScreen.tsx" />

                <div className={styles.submitWrap}>
                    <PrimaryButton type="submit" disabled={isLoading} data-sentry-element="PrimaryButton" data-sentry-source-file="SignUpScreen.tsx">
                        {isLoading ? <Spinner animation="border" size="sm" /> : t('auth.signUp.submit')}
                    </PrimaryButton>
                </div>

                <Controller name="agreed" control={control} render={({
        field
      }) => <>
                            <div className={styles.terms}>
                                <button type="button" role="checkbox" aria-checked={!!field.value} className={`${styles.checkbox} ${field.value ? styles.checked : ''}`} onClick={() => field.onChange(!field.value)}>
                                    <Check size={12} strokeWidth={3} />
                                </button>
                                <p className={styles.termsText}>
                                    {t('auth.signUp.terms')}{' '}
                                    <a onClick={() => setShow(true)} rel="noopener noreferrer" className='link'>
                                        {t('footer.links.terms')}
                                    </a>{' '}
                                    &{' '}
                                    <a href={privacyPolicyLink || '#'} target="_blank" rel="noopener noreferrer">
                                        {t('footer.links.privacy')}
                                    </a>
                                </p>
                            </div>
                            {errors.agreed?.message && <p className={styles.termsError}>
                                    {errors.agreed.message}
                                </p>}
                        </>} data-sentry-element="Controller" data-sentry-source-file="SignUpScreen.tsx" />
            </form>
            <Modal show={show} onHide={() => setShow(false)} size="lg" data-sentry-element="Modal" data-sentry-source-file="SignUpScreen.tsx">
                <Modal.Header closeButton className={styles.modalHeader} data-sentry-element="unknown" data-sentry-source-file="SignUpScreen.tsx">
                    <Modal.Title data-sentry-element="unknown" data-sentry-source-file="SignUpScreen.tsx">
                        <div className={styles.termsAndConditions}>
                            <div className={styles.leftSection}>
                                    <button type="button" className={styles.button} aria-haspopup="menu" aria-label={t('booking.bookingButton')}>
                                        <Image src="/assets/icons/termsncondition.svg" style={{
                  position: 'absolute'
                }} width={18} height={18} alt="termsncondition" className="mx-1 right-icon" data-sentry-element="Image" data-sentry-source-file="SignUpScreen.tsx" />
                                    </button>
                            </div>
                            <div className={styles.rightSection}>
                                <span className={styles.title}>{t('auth.termsAndConditions.title')}</span>
                                <span className={styles.subTitle}>{t('auth.termsAndConditions.subTitle')}</span>
                            </div>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className={styles.modalBody} data-sentry-element="unknown" data-sentry-source-file="SignUpScreen.tsx">
                    <TermsAndConditions mode="popup" onClose={() => setShow(false)} data-sentry-element="TermsAndConditions" data-sentry-source-file="SignUpScreen.tsx" />
                </Modal.Body>
            </Modal>
        </>;
};
export default SignUpScreen;